import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, useScroll, useTransform } from 'framer-motion';
import { shedService } from '../services/shedService';
import { SHED_MODELS } from '../constants/shedModels';

/**
 * Landing page — editorial / architectural "premium prefab" direction.
 * Dark cinematic canvas, oversized Roboto Black display type, Home Depot orange
 * as the action accent and a warm bone tone as the editorial accent. Real photography
 * throughout; live inventory + model data drive the content.
 */

const HERO_IMAGE = '/images/hero-shed.webp';
const PHOTO = '/assets/assets/images';

// Branded per-model photography (keyed by slug). Sourced from the
// per-model image folders under /assets/assets/images/<model>/.
const MODEL_PHOTO = {
  uptown: `${PHOTO}/uptown/The%20Uptown.webp`,
  verde: `${PHOTO}/verde/The%20Verde.webp`,
  desperado: `${PHOTO}/desperado/Desperado.webp`,
  ranger: `${PHOTO}/ranger/The%20Ranger.webp`,
  spartan: `${PHOTO}/spartan/Spartan.webp`,
  chisholm: `${PHOTO}/chisholm/The%20Chisholm.webp`,
  gridiron: `${PHOTO}/gridiron/GridIron.webp`,
  element: `${PHOTO}/element/Element.webp`,
};

const GALLERY = [
  `${PHOTO}/10x20-studio-shed-2of4.webp`,
  `${PHOTO}/12x24-shed.webp`,
  `${PHOTO}/shed-with-deck.webp`,
  `${PHOTO}/10x20-studio-shed-3of4.webp`,
  `${PHOTO}/12x24-a-frameshed.webp`,
  `${PHOTO}/12x24-custom-homeshed.webp`,
];

const TICKER = [
  'Free local delivery',
  'Build online in minutes',
  'Secured by Stripe',
  'Custom paint & roofing',
  'In-stock & ready to go',
  'Reserve with a deposit',
];

const formatPrice = (value) => {
  const n = Number(value);
  if (!n || Number.isNaN(n)) return '$0';
  return `$${n.toLocaleString('en-US')}`;
};

const ease = [0.22, 1, 0.36, 1];
const reveal = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease } },
};
const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
};

const Kicker = ({ children, className = '', ...rest }) => (
  <span
    className={`inline-flex items-center gap-2 font-sans text-[11px] font-bold uppercase tracking-[0.28em] ${className}`}
    {...rest}
  >
    {children}
  </span>
);

const HomeShed = () => {
  const [featured, setFeatured] = useState([]);
  const [featuredLoading, setFeaturedLoading] = useState(true);
  const [liveCount, setLiveCount] = useState(null);

  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 600], [0, 140]);
  const heroScale = useTransform(scrollY, [0, 600], [1, 1.12]);

  useEffect(() => {
    let active = true;
    (async () => {
      const { data } = await shedService.getFeaturedSheds(6);
      if (active) {
        setFeatured(data || []);
        setFeaturedLoading(false);
      }
    })();
    (async () => {
      const { count } = await shedService.getAvailableCount();
      if (active) setLiveCount(count);
    })();
    return () => { active = false; };
  }, []);

  return (
    <div
      className="lp-root font-sans"
      style={{
        '--ink': '#0d0c0b',
        '--ink-2': '#171512',
        '--paper': '#f3efe6',
        '--orange': '#f96302',
        '--orange-bright': '#ff8a3d',
        '--bone': '#e8dfd5',
        '--bone-soft': '#f2ece5',
        background: 'var(--ink)',
        color: '#eef1f7',
      }}
    >
      <Helmet>
        <title>Home Depot Sheds — Custom Outdoor Structures, Built to Order</title>
        <meta
          name="description"
          content="Design a custom shed online or shop in-stock barn cabins, garden sheds and modern studios. Free local delivery, secure checkout, reserve with a deposit."
        />
      </Helmet>

      <style>{`
        .lp-root { overflow-x: hidden; }
        .lp-display { font-family: Roboto, system-ui, sans-serif; font-weight: 900; }
        .lp-grain::before {
          content: ''; position: absolute; inset: 0; pointer-events: none; z-index: 2;
          opacity: 0.5; mix-blend-mode: overlay;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E");
        }
        .lp-grid-lines {
          background-image: linear-gradient(90deg, rgba(255,255,255,.05) 1px, transparent 1px);
          background-size: 8.33vw 100%;
        }
        @keyframes lp-marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        .lp-marquee-track { display: inline-flex; white-space: nowrap; animation: lp-marquee 32s linear infinite; }
        @keyframes lp-pulse { 0%,100% { opacity: 1; } 50% { opacity: .25; } }
        .lp-dot { animation: lp-pulse 1.8s ease-in-out infinite; }
        @keyframes lp-bob { 0%,100% { transform: translateY(0); } 50% { transform: translateY(8px); } }
        .lp-bob { animation: lp-bob 2.4s ease-in-out infinite; }
        .lp-card-img { transition: transform .9s cubic-bezier(.22,1,.36,1); }
        .group:hover .lp-card-img { transform: scale(1.07); }
        .lp-link-underline { background-image: linear-gradient(var(--bone),var(--bone)); background-size: 0% 2px; background-repeat: no-repeat; background-position: 0 100%; transition: background-size .4s cubic-bezier(.22,1,.36,1); }
        .group:hover .lp-link-underline, .lp-link-underline:hover { background-size: 100% 2px; }
      `}</style>

      {/* ============================ HERO ============================ */}
      <section className="lp-grain relative min-h-[100svh] flex flex-col overflow-hidden">
        {/* Background photo with parallax */}
        <motion.div style={{ y: heroY, scale: heroScale }} className="absolute inset-0 z-0">
          <img src={HERO_IMAGE} alt="" aria-hidden="true" className="w-full h-full object-cover" />
        </motion.div>
        {/* Gradient + glow overlays */}
        <div
          className="absolute inset-0 z-[1]"
          style={{
            background:
              'linear-gradient(180deg, rgba(13,12,11,.72) 0%, rgba(13,12,11,.45) 35%, rgba(13,12,11,.78) 78%, var(--ink) 100%)',
          }}
        />
        <div
          className="absolute inset-0 z-[1]"
          style={{ background: 'radial-gradient(60% 50% at 18% 30%, rgba(37,99,235,.35), transparent 70%)' }}
        />
        <div className="lp-grid-lines absolute inset-0 z-[1] opacity-60" />

        {/* Top meta bar */}
        <div className="relative z-[3] container mx-auto px-5 pt-24 lg:pt-28">
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease }}
            className="flex items-center justify-between border-b border-white/15 pb-4"
          >
            <Kicker style={{ color: 'var(--bone-soft)' }}>
              <span style={{ color: 'var(--bone)' }}>Home Depot Sheds</span>
              <span className="text-white/40">— Custom Outdoor Structures</span>
            </Kicker>
            <Kicker className="text-white/70">
              <span className="lp-dot w-1.5 h-1.5 rounded-full" style={{ background: 'var(--orange-bright)' }} />
              {liveCount === null ? '— in stock' : `${liveCount} in stock now`}
            </Kicker>
          </motion.div>
        </div>

        {/* Headline block */}
        <div className="relative z-[3] container mx-auto px-5 flex-1 flex flex-col justify-center py-14">
          <motion.div variants={stagger} initial="hidden" animate="show" className="max-w-5xl">
            <motion.h1
              variants={reveal}
              className="lp-display uppercase leading-[0.86] tracking-[-0.03em]"
              style={{ fontSize: 'clamp(3rem, 11vw, 9.5rem)' }}
            >
              <span className="block">Built to order.</span>
              <span className="block">
                Delivered to your{' '}
                <span style={{ WebkitTextStroke: '2px var(--bone)', color: 'transparent' }}>yard.</span>
              </span>
            </motion.h1>

            <motion.p
              variants={reveal}
              className="mt-8 max-w-xl text-lg md:text-xl text-white/75 leading-relaxed"
            >
              Design a shed online — garden sheds, barn cabins, gable cabins and modern
              studios — or shop what's already built and ready to roll out.
            </motion.p>

            <motion.div variants={reveal} className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/inventory"
                className="group relative inline-flex items-center gap-3 px-9 py-4 rounded-full font-semibold text-white overflow-hidden"
                style={{ background: 'var(--orange)' }}
              >
                <span className="relative z-10">Build your shed</span>
                <span className="relative z-10 transition-transform group-hover:translate-x-1">→</span>
                <span
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{ background: 'linear-gradient(90deg, var(--orange), var(--orange-bright))' }}
                />
              </Link>
              <Link
                to="/inventory"
                className="inline-flex items-center gap-2 px-2 py-4 font-semibold text-white/90"
              >
                <span className="lp-link-underline">Browse in-stock sheds</span>
              </Link>
            </motion.div>
          </motion.div>
        </div>

        {/* Bottom hero chrome */}
        <div className="relative z-[3] container mx-auto px-5 pb-8 flex items-end justify-between">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8, duration: 1 }}
            className="font-sans text-[11px] uppercase tracking-[0.28em] text-white/40"
          >
            01 — Free delivery on every shed
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 1 }}
            className="lp-bob hidden md:flex flex-col items-center gap-2 text-white/50"
          >
            <span className="font-sans text-[10px] uppercase tracking-[0.3em]">Scroll</span>
            <span className="w-px h-10" style={{ background: 'linear-gradient(var(--bone), transparent)' }} />
          </motion.div>
        </div>
      </section>

      {/* ============================ MARQUEE ============================ */}
      <div
        className="relative border-y overflow-hidden py-4"
        style={{ borderColor: 'rgba(255,255,255,.1)', background: 'var(--ink-2)' }}
      >
        <div className="lp-marquee-track">
          {[...TICKER, ...TICKER].map((t, i) => (
            <span key={i} className="inline-flex items-center">
              <span className="lp-display uppercase text-2xl md:text-3xl tracking-tight px-7 text-white/85">
                {t}
              </span>
              <span className="text-xl" style={{ color: 'var(--bone)' }}>✦</span>
            </span>
          ))}
        </div>
      </div>

      {/* ============================ PROCESS (light band) ============================ */}
      <section className="relative" style={{ background: 'var(--paper)', color: '#1a1407' }}>
        <div className="lp-grain absolute inset-0 opacity-[0.4]" />
        <div className="relative container mx-auto px-5 py-20 md:py-28">
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: '-80px' }}
          >
            <motion.div variants={reveal}>
              <Kicker style={{ color: '#9a6b1f' }}>04 — How it works</Kicker>
            </motion.div>
            <motion.h2
              variants={reveal}
              className="lp-display uppercase tracking-[-0.02em] leading-[0.9] mt-3 max-w-3xl"
              style={{ fontSize: 'clamp(2.2rem, 6vw, 4.5rem)', color: '#15100a' }}
            >
              Design it online. We build &amp; deliver.
            </motion.h2>

            <div className="grid md:grid-cols-3 gap-px mt-14" style={{ background: 'rgba(26,20,7,.14)' }}>
              {[
                {
                  n: '01',
                  t: 'Configure your build',
                  d: 'Pick the model, size, paint, roofing and add-ons in our online configurator — see the price update live.',
                },
                {
                  n: '02',
                  t: 'Reserve with a deposit',
                  d: 'Lock it in through secure Stripe checkout. A deposit reserves your build; the balance is due at delivery.',
                },
                {
                  n: '03',
                  t: 'Free local delivery',
                  d: "We build it and roll it out to your yard. In-stock sheds ship fast — no freight surprises.",
                },
              ].map((step) => (
                <motion.div
                  variants={reveal}
                  key={step.n}
                  className="p-8 md:p-10"
                  style={{ background: 'var(--paper)' }}
                >
                  <span className="lp-display text-5xl" style={{ color: 'var(--bone)' }}>{step.n}</span>
                  <h3 className="font-heading font-bold text-xl mt-5" style={{ color: '#15100a' }}>{step.t}</h3>
                  <p className="mt-3 text-[15px] leading-relaxed" style={{ color: '#5b5345' }}>{step.d}</p>
                </motion.div>
              ))}
            </div>

            <motion.div variants={reveal} className="mt-12 flex flex-wrap items-center gap-6">
              <Link
                to="/inventory"
                className="group inline-flex items-center gap-3 px-9 py-4 rounded-full font-semibold text-white"
                style={{ background: '#15100a' }}
              >
                Start your build
                <span className="transition-transform group-hover:translate-x-1" style={{ color: 'var(--bone-soft)' }}>→</span>
              </Link>
              <span className="text-sm" style={{ color: '#7a7163' }}>
                {liveCount === null ? 'Sheds' : `${liveCount} sheds`} ready right now
              </span>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ============================ SHOP BY STYLE ============================ */}
      <section className="container mx-auto px-5 pb-20 md:pb-28">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-80px' }}
          className="mb-12"
        >
          <motion.div variants={reveal}>
            <Kicker style={{ color: 'var(--bone)' }}>03 — The lineup</Kicker>
          </motion.div>
          <motion.h2
            variants={reveal}
            className="lp-display uppercase tracking-[-0.02em] leading-[0.9] mt-3"
            style={{ fontSize: 'clamp(2.2rem, 6vw, 4.5rem)' }}
          >
            Shop by style
          </motion.h2>
        </motion.div>

        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-60px' }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6"
        >
          {SHED_MODELS.map((model, i) => (
            <motion.div variants={reveal} key={model.slug}>
              <Link
                to={`/inventory?model=${encodeURIComponent(model.name)}`}
                className="group relative block rounded-2xl overflow-hidden aspect-[3/4]"
                style={{ background: 'var(--ink-2)' }}
              >
                <img
                  src={MODEL_PHOTO[model.slug]}
                  alt={`${model.name} — ${model.category}`}
                  loading="lazy"
                  className="lp-card-img absolute inset-0 w-full h-full object-cover opacity-80"
                />
                <div
                  className="absolute inset-0"
                  style={{ background: 'linear-gradient(180deg, rgba(13,12,11,.1) 30%, rgba(13,12,11,.92))' }}
                />
                <span className="absolute top-4 left-4 lp-display text-sm text-white/50">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div className="absolute inset-x-0 bottom-0 p-5">
                  <p className="text-[10px] uppercase tracking-[0.22em] mb-1" style={{ color: 'var(--bone-soft)' }}>
                    {model.category}
                  </p>
                  <h3 className="lp-display uppercase text-xl md:text-2xl leading-none text-white">
                    {model.name}
                  </h3>
                  <p className="text-xs text-white/55 mt-2">
                    {model.sizes.length} sizes
                    <span className="inline-block ml-2 transition-transform group-hover:translate-x-1" style={{ color: 'var(--bone)' }}>→</span>
                  </p>
                </div>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* ============================ IN STOCK NOW ============================ */}
      <section className="container mx-auto px-5 py-20 md:py-28">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-80px' }}
          className="flex flex-wrap items-end justify-between gap-4 mb-12"
        >
          <div>
            <motion.div variants={reveal}>
              <Kicker style={{ color: 'var(--bone)' }}>02 — Ready to roll out</Kicker>
            </motion.div>
            <motion.h2
              variants={reveal}
              className="lp-display uppercase tracking-[-0.02em] leading-[0.9] mt-3"
              style={{ fontSize: 'clamp(2.2rem, 6vw, 4.5rem)' }}
            >
              In stock now
            </motion.h2>
          </div>
          <motion.div variants={reveal}>
            <Link to="/inventory" className="group inline-flex items-center gap-2 font-semibold text-white/80">
              <span className="lp-link-underline">View all inventory</span>
              <span className="transition-transform group-hover:translate-x-1" style={{ color: 'var(--bone)' }}>→</span>
            </Link>
          </motion.div>
        </motion.div>

        {featuredLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="rounded-2xl overflow-hidden animate-pulse" style={{ background: 'var(--ink-2)' }}>
                <div className="aspect-[4/3]" style={{ background: 'rgba(255,255,255,.04)' }} />
                <div className="p-5">
                  <div className="h-5 rounded w-2/3 mb-3" style={{ background: 'rgba(255,255,255,.06)' }} />
                  <div className="h-4 rounded w-1/3" style={{ background: 'rgba(255,255,255,.06)' }} />
                </div>
              </div>
            ))}
          </div>
        ) : featured.length === 0 ? (
          <div
            className="rounded-2xl border p-12 text-center"
            style={{ borderColor: 'rgba(255,255,255,.12)', background: 'var(--ink-2)' }}
          >
            <p className="text-white/70 text-lg">
              Nothing's on the lot this minute — design your own and we'll build it.
            </p>
            <Link
              to="/inventory"
              className="inline-flex mt-6 px-7 py-3 rounded-full font-semibold text-white"
              style={{ background: 'var(--orange)' }}
            >
              Start building →
            </Link>
          </div>
        ) : (
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: '-60px' }}
            className="flex gap-6 overflow-x-auto snap-x md:grid md:grid-cols-3 md:overflow-visible pb-2 -mx-5 px-5 md:mx-0 md:px-0"
          >
            {featured.map((shed) => {
              const price = shed.salePrice && shed.salePrice < shed.price ? shed.salePrice : shed.price;
              const onSale = shed.salePrice && shed.salePrice < shed.price;
              return (
                <motion.div variants={reveal} key={shed.id} className="snap-start shrink-0 w-[78vw] sm:w-72 md:w-auto">
                  <Link
                    to={`/shed/${shed.id}`}
                    className="group block rounded-2xl overflow-hidden border h-full"
                    style={{ borderColor: 'rgba(255,255,255,.1)', background: 'var(--ink-2)' }}
                  >
                    <div className="relative aspect-[4/3] overflow-hidden" style={{ background: 'rgba(255,255,255,.04)' }}>
                      {shed.images?.[0] && (
                        <img
                          src={shed.images[0]}
                          alt={`${shed.model || 'Shed'} ${shed.size || ''}`}
                          className="lp-card-img w-full h-full object-cover"
                        />
                      )}
                      {onSale && (
                        <span
                          className="absolute top-3 left-3 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider text-black"
                          style={{ background: 'var(--bone)' }}
                        >
                          On sale
                        </span>
                      )}
                      <div
                        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity"
                        style={{ background: 'linear-gradient(180deg, transparent 50%, rgba(13,12,11,.6))' }}
                      />
                    </div>
                    <div className="p-5 flex items-center justify-between">
                      <div>
                        <h3 className="font-bold text-white leading-tight">
                          {[shed.model, shed.size].filter(Boolean).join(' ') || shed.name}
                        </h3>
                        <p className="text-[11px] uppercase tracking-[0.2em] text-white/40 mt-1">Ready to deliver</p>
                      </div>
                      <p className="lp-display text-xl" style={{ color: 'var(--bone-soft)' }}>
                        {formatPrice(price)}
                      </p>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </section>

      {/* ============================ GALLERY ============================ */}
      <section className="container mx-auto px-5 py-20 md:py-28">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-80px' }}
          className="mb-10"
        >
          <motion.div variants={reveal}>
            <Kicker style={{ color: 'var(--bone)' }}>05 — Real builds</Kicker>
          </motion.div>
          <motion.h2
            variants={reveal}
            className="lp-display uppercase tracking-[-0.02em] leading-[0.9] mt-3"
            style={{ fontSize: 'clamp(2.2rem, 6vw, 4.5rem)' }}
          >
            On the lot
          </motion.h2>
        </motion.div>

        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-60px' }}
          className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 auto-rows-[42vw] md:auto-rows-[19vw]"
        >
          {GALLERY.map((src, i) => (
            <motion.div
              variants={reveal}
              key={src}
              className={`group relative overflow-hidden rounded-xl ${i === 0 ? 'col-span-2 row-span-2' : ''}`}
              style={{ background: 'var(--ink-2)' }}
            >
              <img src={src} alt="Custom shed build" loading="lazy" className="lp-card-img w-full h-full object-cover opacity-90" />
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: 'rgba(37,99,235,.18)' }} />
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* ============================ CTA BAND ============================ */}
      <section className="relative overflow-hidden lp-grain" style={{ background: 'var(--orange)' }}>
        <div
          className="absolute inset-0"
          style={{ background: 'radial-gradient(70% 120% at 80% 0%, rgba(255,255,255,.22), transparent 60%)' }}
        />
        <div className="lp-grid-lines absolute inset-0 opacity-30" />
        <div className="relative container mx-auto px-5 py-24 md:py-32 text-center">
          <motion.div
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: '-80px' }}
          >
            <motion.div variants={reveal}>
              <Kicker className="text-white/70">Your yard, your build</Kicker>
            </motion.div>
            <motion.h2
              variants={reveal}
              className="lp-display uppercase tracking-[-0.02em] leading-[0.88] mt-4 mx-auto max-w-4xl text-white"
              style={{ fontSize: 'clamp(2.6rem, 8vw, 6.5rem)' }}
            >
              Design the shed you actually want.
            </motion.h2>
            <motion.div variants={reveal} className="mt-10 flex flex-wrap items-center justify-center gap-4">
              <Link
                to="/inventory"
                className="group inline-flex items-center gap-3 px-10 py-5 rounded-full font-bold text-[var(--orange)] bg-white hover:bg-[var(--paper)] transition-colors"
              >
                Build your shed
                <span className="transition-transform group-hover:translate-x-1">→</span>
              </Link>
              <Link
                to="/inventory"
                className="inline-flex items-center gap-2 px-2 py-5 font-semibold text-white"
              >
                <span className="lp-link-underline" style={{ backgroundImage: 'linear-gradient(#fff,#fff)' }}>
                  or browse what's in stock
                </span>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default HomeShed;
